import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useFormik } from 'formik';
import { employeeSchema } from 'schema';
import { FormLabel, FormControl, FormHelperText, IconButton, InputAdornment, Box, Divider, useTheme } from '@mui/material';
import { postApi } from 'views/services/api';
import PropTypes from 'prop-types';

import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import BadgeIcon from '@mui/icons-material/Badge';
import WorkIcon from '@mui/icons-material/Work';
import ContactPhoneIcon from '@mui/icons-material/ContactPhone';

import { toast } from 'react-toastify';
import { useState } from 'react';

const AddEmployee = (props) => {
  const { open, handleClose } = props;
  const theme = useTheme();
  const [showNewPassword, setShowNewPassword] = useState(false);

  const initialValues = {
    employeeType: 'default',
    shift: 'default',
    phoneNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    idCardType: 'default',
    idcardNumber: '',
    address: '',
    idFile: null,
    idFile2: null,
    salary: '',
    role: 'Staff'
  };

  const AddData = async (values) => {
    try {
      const formData = new FormData();
      formData.append('firstName', values.firstName);
      formData.append('lastName', values.lastName);

      formData.append('email', values.email);
      formData.append('password', values.password);

      formData.append('phoneNumber', values.phoneNumber);
      formData.append('shift', values.shift);
      formData.append('hotelId', values.hotelId);
      formData.append('employeeType', values.employeeType);
      formData.append('idCardType', values.idCardType);
      formData.append('idcardNumber', values.idcardNumber);
      formData.append('address', values.address);
      formData.append('salary', values.salary);
      
      if (values.idFile) {
        formData.append('idFile', values.idFile);
      }
      if (values.idFile2) {
        formData.append('idFile2', values.idFile2);
      }
      formData.append('role', values.employeeType);

      //-------------------------------------
      let response = await postApi('api/employee/add', formData);
      if (response.status === 200) {
        toast.success('Empleado agregado exitosamente');
        handleClose();
      } else {
        toast.error('No se pudo agregar el empleado');
      }
    } catch (e) {
      console.log(e);
      toast.error('Ocurrió un error al agregar el empleado');
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: employeeSchema,
    onSubmit: async (values, { resetForm }) => {
      values.hotelId = JSON.parse(localStorage.getItem('hotelData')).hotelId;
      AddData(values);
      resetForm();
    }
  });

  return (
    <Dialog 
      open={open} 
      maxWidth="md"
      fullWidth
      aria-labelledby="scroll-dialog-title" 
      aria-describedby="scroll-dialog-description"
      PaperProps={{
        style: { borderRadius: 12 }
      }}
    >
      <DialogTitle
        id="scroll-dialog-title"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
          Agregar Nuevo Empleado
        </Typography>
      </DialogTitle>
      
      <Divider />

      <DialogContent sx={{ p: 4 }}>
        <form onSubmit={formik.handleSubmit} encType="multipart/form-data">
          <Grid container rowSpacing={3} columnSpacing={3}>
            
            {/* Información Laboral */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <WorkIcon sx={{ color: theme.palette.text.secondary, mr: 1 }} />
                <Typography variant="h6" color="textSecondary">Información Laboral</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1, fontWeight: 500 }}>Cargo / Rol</FormLabel>
                <Select
                  id="employeeType"
                  name="employeeType"
                  size="medium"
                  fullWidth
                  value={formik.values.employeeType}
                  onChange={formik.handleChange}
                  error={formik.touched.employeeType && Boolean(formik.errors.employeeType)}
                >
                  <MenuItem value="default" disabled>Seleccione el Cargo</MenuItem>
                  <MenuItem value="manager">Gerente</MenuItem>
                  <MenuItem value="cleaning">Limpieza</MenuItem>
                  <MenuItem value="reception">Recepción</MenuItem>
                  <MenuItem value="cook">Cocinero</MenuItem>
                  <MenuItem value="waiter">Mesero</MenuItem>
                </Select>
                {formik.touched.employeeType && formik.errors.employeeType && (
                  <FormHelperText error>{formik.errors.employeeType}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1, fontWeight: 500 }}>Turno de Trabajo</FormLabel>
                <Select
                  id="shift"
                  name="shift"
                  size="medium"
                  fullWidth
                  value={formik.values.shift}
                  onChange={formik.handleChange}
                  error={formik.touched.shift && Boolean(formik.errors.shift)}
                >
                  <MenuItem value="default" disabled>Seleccione el Turno</MenuItem>
                  <MenuItem value="Day-Shift 08:00 AM - 08:00 PM">Turno Día (08:00 AM - 08:00 PM)</MenuItem>
                  <MenuItem value="Night-Shift 08:00 PM - 08:00 AM">Turno Noche (08:00 PM - 08:00 AM)</MenuItem>
                </Select>
                {formik.touched.shift && formik.errors.shift && (
                  <FormHelperText error>{formik.errors.shift}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Información Personal */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <BadgeIcon sx={{ color: theme.palette.text.secondary, mr: 1 }} />
                <Typography variant="h6" color="textSecondary">Información Personal</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, fontWeight: 500, display: 'block' }}>Nombres</FormLabel>
              <TextField
                id="firstName"
                name="firstName"
                size="medium"
                fullWidth
                placeholder="Ej. Juan Andrés"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                helperText={formik.touched.firstName && formik.errors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, fontWeight: 500, display: 'block' }}>Apellidos</FormLabel>
              <TextField
                id="lastName"
                name="lastName"
                size="medium"
                fullWidth
                placeholder="Ej. Pérez García"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1, fontWeight: 500 }}>Tipo de Documento</FormLabel>
                <Select
                  id="idCardType"
                  name="idCardType"
                  size="medium"
                  fullWidth
                  value={formik.values.idCardType}
                  onChange={formik.handleChange}
                  error={formik.touched.idCardType && Boolean(formik.errors.idCardType)}
                >
                  <MenuItem value="default" disabled>Seleccione el Documento</MenuItem>
                  <MenuItem value="ID">Cédula de Identidad</MenuItem>
                  <MenuItem value="Passport">Pasaporte</MenuItem>
                  <MenuItem value="Rif">RIF</MenuItem>
                  <MenuItem value="DrivingLicense">Licencia de Conducir</MenuItem>
                </Select>
                <FormHelperText error={formik.touched.idCardType && Boolean(formik.errors.idCardType)}>
                  {formik.touched.idCardType && formik.errors.idCardType}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, fontWeight: 500, display: 'block' }}>Número de Documento</FormLabel>
              <TextField
                id="idcardNumber"
                name="idcardNumber"
                size="medium"
                fullWidth
                placeholder="Ej. V-12345678"
                value={formik.values.idcardNumber}
                onChange={formik.handleChange}
                error={formik.touched.idcardNumber && Boolean(formik.errors.idcardNumber)}
                helperText={formik.touched.idcardNumber && formik.errors.idcardNumber}
              />
            </Grid>

            {/* Documentos (Opcionales) */}
            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, fontWeight: 500, display: 'block' }}>Documento Identidad (Frente) - Opcional</FormLabel>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<UploadFileIcon />}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1 }}
                color={formik.values.idFile ? "success" : "primary"}
              >
                {formik.values.idFile ? formik.values.idFile.name : "Subir Archivo"}
                <input
                  type="file"
                  hidden
                  onChange={(event) => formik.setFieldValue('idFile', event.currentTarget.files[0])}
                />
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, fontWeight: 500, display: 'block' }}>Documento Identidad (Reverso) - Opcional</FormLabel>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<UploadFileIcon />}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1 }}
                color={formik.values.idFile2 ? "success" : "primary"}
              >
                {formik.values.idFile2 ? formik.values.idFile2.name : "Subir Archivo"}
                <input
                  type="file"
                  hidden
                  onChange={(event) => formik.setFieldValue('idFile2', event.currentTarget.files[0])}
                />
              </Button>
            </Grid>

            {/* Contacto & Credenciales */}
            <Grid item xs={12} sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ContactPhoneIcon sx={{ color: theme.palette.text.secondary, mr: 1 }} />
                <Typography variant="h6" color="textSecondary">Contacto & Credenciales</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, fontWeight: 500, display: 'block' }}>Correo Electrónico</FormLabel>
              <TextField
                id="email"
                name="email"
                size="medium"
                fullWidth
                placeholder="correo@ejemplo.com"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, fontWeight: 500, display: 'block' }}>Contraseña de Acceso</FormLabel>
              <TextField
                id="password"
                name="password"
                size="medium"
                type={showNewPassword ? 'text' : 'password'}
                fullWidth
                placeholder="Ingrese una contraseña"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                        {showNewPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, fontWeight: 500, display: 'block' }}>Teléfono</FormLabel>
              <TextField
                id="phoneNumber"
                name="phoneNumber"
                size="medium"
                type="text"
                fullWidth
                placeholder="Ej. 04141234567"
                value={formik.values.phoneNumber}
                onChange={(e) => {
                  const phoneNumber = e.target.value.replace(/\D/g, '').slice(0, 15);
                  formik.setFieldValue('phoneNumber', phoneNumber);
                }}
                error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormLabel sx={{ mb: 1, fontWeight: 500, display: 'block' }}>Salario Mensual</FormLabel>
              <TextField
                id="salary"
                name="salary"
                size="medium"
                fullWidth
                placeholder="Ej. 300"
                value={formik.values.salary}
                onChange={formik.handleChange}
                error={formik.touched.salary && Boolean(formik.errors.salary)}
                helperText={formik.touched.salary && formik.errors.salary}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormLabel sx={{ mb: 1, fontWeight: 500, display: 'block' }}>Dirección de Residencia</FormLabel>
              <TextField
                id="address"
                name="address"
                size="medium"
                fullWidth
                multiline
                rows={2}
                placeholder="Ingrese la dirección completa"
                value={formik.values.address}
                onChange={formik.handleChange}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
              />
            </Grid>

          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
            <Button onClick={handleClose} variant="outlined" color="error" size="large">
              Cancelar
            </Button>
            <Button type="submit" variant="contained" color="primary" size="large">
              Guardar Empleado
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

AddEmployee.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired
};

export default AddEmployee;

